import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Target, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * MacroGoalEditor — lets users set daily targets for all 7 macros from their profile.
 * Backend supports: calorie_goal, protein_goal_g, carbs_goal_g, fat_goal_g,
 * sugar_goal_g, fiber_goal_g, sodium_goal_mg (all optional / nullable).
 *
 * Includes a "Suggest" button that auto-fills macro splits from calorie_goal
 * (30% protein, 45% carbs, 25% fat) and applies sane WHO/FDA defaults for the rest.
 */
const FIELDS = [
  { key: "calorie_goal",   label: "Calories",  unit: "kcal", color: "text-emerald-400", pillBg: "bg-emerald-400" },
  { key: "protein_goal_g", label: "Protein",   unit: "g",    color: "text-rose-400",    pillBg: "bg-rose-400" },
  { key: "carbs_goal_g",   label: "Carbs",     unit: "g",    color: "text-blue-400",    pillBg: "bg-blue-400" },
  { key: "fat_goal_g",     label: "Fat",       unit: "g",    color: "text-orange-400",  pillBg: "bg-orange-400" },
  { key: "sugar_goal_g",   label: "Sugar cap", unit: "g",    color: "text-pink-400",    pillBg: "bg-pink-400" },
  { key: "fiber_goal_g",   label: "Fiber",     unit: "g",    color: "text-violet-400",  pillBg: "bg-violet-400" },
  { key: "sodium_goal_mg", label: "Sodium cap",unit: "mg",   color: "text-slate-400",   pillBg: "bg-slate-400" },
];

export default function MacroGoalEditor() {
  const { user, refreshUser } = useAuth();
  const [values, setValues] = useState({
    calorie_goal: "", protein_goal_g: "", carbs_goal_g: "", fat_goal_g: "",
    sugar_goal_g: "", fiber_goal_g: "", sodium_goal_mg: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setValues({
      calorie_goal: user.calorie_goal ?? "",
      protein_goal_g: user.protein_goal_g ?? "",
      carbs_goal_g: user.carbs_goal_g ?? "",
      fat_goal_g: user.fat_goal_g ?? "",
      sugar_goal_g: user.sugar_goal_g ?? "",
      fiber_goal_g: user.fiber_goal_g ?? "",
      sodium_goal_mg: user.sodium_goal_mg ?? "",
    });
  }, [user]);

  const suggestSplits = () => {
    const cal = Number(values.calorie_goal);
    if (!cal || cal <= 0) {
      toast.error("Set a calorie goal first, then Suggest will fill the rest");
      return;
    }
    setValues((v) => ({
      ...v,
      protein_goal_g: Math.round((cal * 0.30) / 4),
      carbs_goal_g:   Math.round((cal * 0.45) / 4),
      fat_goal_g:     Math.round((cal * 0.25) / 9),
      sugar_goal_g:   v.sugar_goal_g || 50,
      fiber_goal_g:   v.fiber_goal_g || 30,
      sodium_goal_mg: v.sodium_goal_mg || 2300,
    }));
    toast.success("Filled from 30/45/25 split — review and save");
  };

  const save = async () => {
    setSaving(true);
    const payload = {};
    for (const f of FIELDS) {
      const raw = values[f.key];
      if (raw === "" || raw == null) {
        payload[f.key] = null; // explicitly clear
      } else {
        const n = Number(raw);
        if (Number.isNaN(n) || n < 0) continue;
        payload[f.key] = n;
      }
    }
    try {
      await api.patch("/users/me", payload);
      await refreshUser?.();
      toast.success("Macro goals saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className="bg-white border border-slate-200 rounded-none p-5"
      data-testid="macro-goal-editor"
    >
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[var(--amino-teal)]">// Preferences · Nutrition</div>
          <div className="font-display font-black uppercase tracking-tight text-xl leading-none mt-1 flex items-center gap-2">
            <Target className="w-4 h-4 text-[#0D9488]" /> Daily Macro Targets
          </div>
        </div>
        <button
          type="button"
          onClick={suggestSplits}
          className="flex items-center gap-1 border border-teal-800 bg-teal-950/30 text-teal-300 px-2.5 py-1.5 font-mono-num text-[10px] uppercase tracking-widest hover:bg-teal-900/40"
          data-testid="macro-goal-suggest"
        >
          <Sparkles className="w-3 h-3" /> Suggest
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        These targets drive the progress bars on your Nutrition dashboard. Leave blank to auto-derive from calories.
      </p>
      <div className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <Label className={`text-[10px] uppercase tracking-[0.2em] font-mono-num ${f.color} flex items-center gap-1`}>
                <span className={`w-1.5 h-1.5 rounded-full ${f.pillBg}`} />{f.label}
              </Label>
              <Input
                type="number" step={f.unit === "kcal" ? "1" : "0.1"} min="0" inputMode="decimal"
                value={values[f.key] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                placeholder="—"
                className="bg-slate-50 border-slate-200 rounded-none h-9 font-mono-num text-sm mt-1"
                data-testid={`macro-goal-${f.key}`}
              />
              <div className="text-[9px] font-mono-num uppercase tracking-widest text-slate-400 mt-0.5">{f.unit}</div>
            </div>
          ))}
        </div>
        <Button
          type="button"
          onClick={save}
          disabled={saving}
          className="w-full h-10 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest text-xs font-semibold"
          data-testid="macro-goal-save"
        >
          {saving ? "Saving…" : "Save targets"}
        </Button>
      </div>
    </Card>
  );
}
